/**
 * Shared ready/scroll/resize dispatcher for LFC's custom sections.
 *
 * Problem this solves: many sections each registered their own raw
 * `window.addEventListener('scroll', ...)` / `('resize', ...)`, so a single
 * page stacking several of them (a sport-hub page alone repeats
 * lfc-sport-product-grid six times) ran that many separate listeners, each
 * doing its own throttling. This file is loaded once, deferred, in
 * layout/theme.liquid (same spot as global.js) and gives every section ONE
 * shared listener to subscribe a callback to instead.
 *
 * Usage from a section's own inline <script>, unchanged from the existing
 * per-file pattern - still wrapped in the section's own init function, still
 * triggered by DOMContentLoaded / shopify:section:load:
 *
 *   const unsubscribe = window.LFCEvents.onScroll(myScrollHandler);
 *   const unsubscribe = window.LFCEvents.onResize(myResizeHandler);
 *
 * Deliberately a separate global from window.LFC - that namespace already
 * belongs to the League Lab mission/phase state system (LFC.events,
 * LFC.state), and conflating an unrelated generic dispatcher into it would
 * just be confusing, even though both happen to use the same safe
 * `window.X = window.X || {}` merge idiom.
 *
 * Safe-by-construction: this script is deferred, so by the time any
 * section's own DOMContentLoaded-triggered init runs, window.LFCEvents
 * already exists (deferred scripts always finish before DOMContentLoaded
 * fires) - exactly the same guarantee every section already relies on for
 * global.js.
 */
window.LFCEvents = window.LFCEvents || {};

(function (ns) {
  // ---- Shared scroll dispatcher: one passive listener, rAF-throttled, many subscribers ----
  const scrollFns = new Set();
  let scrollTicking = false;

  function dispatchScroll() {
    scrollTicking = false;
    scrollFns.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        // One section's bug shouldn't stop every other section's scroll handler.
      }
    });
  }

  window.addEventListener(
    'scroll',
    function () {
      if (scrollTicking || scrollFns.size === 0) return;
      scrollTicking = true;
      window.requestAnimationFrame(dispatchScroll);
    },
    { passive: true }
  );

  // Returns an unsubscribe function, same shape as a plain removeEventListener call.
  ns.onScroll = function (fn) {
    scrollFns.add(fn);
    return function unsubscribe() {
      scrollFns.delete(fn);
    };
  };

  // ---- Shared resize dispatcher: one debounced listener, many subscribers ----
  const resizeFns = new Set();
  let resizeTimer = null;
  const RESIZE_DEBOUNCE_MS = 150;

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      resizeFns.forEach((fn) => {
        try {
          fn();
        } catch (err) {
          // Same isolation as the scroll dispatcher above.
        }
      });
    }, RESIZE_DEBOUNCE_MS);
  });

  ns.onResize = function (fn) {
    resizeFns.add(fn);
    return function unsubscribe() {
      resizeFns.delete(fn);
    };
  };
})(window.LFCEvents);
